import { useState, useRef } from 'react';

export default function ExcelYukle() {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const ACCEPTED = ['.xlsx', '.xls', '.csv'];

  function isValid(f: File) {
    return ACCEPTED.some((ext) => f.name.toLowerCase().endsWith(ext));
  }

  function handleFile(f: File) {
    if (!isValid(f)) {
      setError('Yalnızca .xlsx, .xls veya .csv dosyaları kabul edilmektedir.');
      setFile(null);
      return;
    }
    setError('');
    setFile(f);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  }

  async function handleUpload() {
  if (!file) return;

  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await fetch("/api/excel_yukle.php", {
      method: "POST",
      body: formData
    });

    const data = await res.json();

    if (data.status === "success") {
      setUploaded(true);
    } else {
      setError(data.message || "Yükleme hatası");
    }

  } catch (err) {
    setError("Sunucu hatası");
  }
}

  function handleReset() {
    setFile(null);
    setUploaded(false);
    setError('');
    if (inputRef.current) inputRef.current.value = '';
  }

  if (uploaded && file) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-xl font-bold text-white">Excel Yükleme</h1>
        </div>
        <div className="max-w-lg mx-auto rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-10 flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-500/20">
            <i className="ri-file-excel-2-line text-3xl text-emerald-400" />
          </div>
          <div>
            <p className="text-white font-bold text-lg">Dosya Başarıyla Yüklendi</p>
            <p className="text-white/40 text-sm mt-1">
              <span className="text-white/70 font-semibold">{file.name}</span> işleme alındı.
            </p>
          </div>
          <button
            onClick={handleReset}
            className="px-6 py-2.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-sm font-semibold transition-all cursor-pointer"
          >
            Yeni Dosya Yükle
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-white">Excel Yükleme</h1>
        <p className="text-white/30 text-sm mt-0.5">Toplu başvuru eklemek için Excel veya CSV dosyası yükleyin</p>
      </div>

      <div className="max-w-2xl space-y-4">
        {/* Drop Zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`rounded-xl border-2 border-dashed p-12 flex flex-col items-center gap-4 text-center cursor-pointer transition-all ${
            dragging
              ? 'border-amber-400/60 bg-amber-500/10'
              : file
              ? 'border-emerald-400/40 bg-emerald-500/[0.06]'
              : 'border-white/[0.10] bg-white/[0.02] hover:border-white/[0.20] hover:bg-white/[0.04]'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleChange}
            className="hidden"
          />
          <div
            className={`w-16 h-16 flex items-center justify-center rounded-xl ${
              file ? 'bg-emerald-500/20' : 'bg-white/[0.05]'
            }`}
          >
            <i
              className={`ri-file-excel-2-line text-3xl ${
                file ? 'text-emerald-400' : dragging ? 'text-amber-400' : 'text-white/20'
              }`}
            />
          </div>
          {file ? (
            <div>
              <p className="text-emerald-400 font-semibold">{file.name}</p>
              <p className="text-white/30 text-xs mt-1">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
          ) : (
            <div>
              <p className="text-white/60 font-semibold">Dosyayı buraya sürükleyin</p>
              <p className="text-white/30 text-sm mt-1">veya tıklayarak seçin</p>
              <p className="text-white/20 text-xs mt-2">.xlsx · .xls · .csv desteklenir</p>
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-rose-500/10 border border-rose-500/20 px-4 py-3">
            <i className="ri-error-warning-line text-rose-400" />
            <p className="text-rose-400 text-sm">{error}</p>
          </div>
        )}

        {/* Format Info */}
        <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-5 space-y-3">
          <p className="text-white/50 text-xs font-semibold uppercase tracking-widest">
            <i className="ri-information-line mr-1.5 text-amber-400" />
            Örnek Format
          </p>
          <div className="bg-black/40 rounded-lg p-4 font-mono text-xs text-white/50 space-y-1">
            <p className="text-amber-400 font-semibold">ad_soyad,telefon</p>
            <p>Ahmet Kaya,+90 532 111 2233</p>
            <p>Selin Arslan,+90 543 222 3344</p>
            <p>Mehmet Demir,+90 505 333 4455</p>
          </div>
          <div className="space-y-1.5">
            {[
              'İlk satır başlık satırı olmalıdır: ad_soyad, telefon',
              'Telefon numaraları uluslararası formatta olabilir',
              'Ek alanlar (segment, is_seviyesi) opsiyoneldir',
              'Maksimum 1.000 satır desteklenir',
            ].map((note) => (
              <div key={note} className="flex items-start gap-2">
                <i className="ri-check-line text-emerald-400 text-xs mt-0.5" />
                <p className="text-white/30 text-xs">{note}</p>
              </div>
            ))}
          </div>
        </div>

        {file && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleUpload}
              className="px-6 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-upload-cloud-2-line mr-2" />
              Dosyayı Yükle
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.08] text-white/40 hover:text-white/70 text-sm font-medium transition-all cursor-pointer"
            >
              İptal
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
